import { ROLES, type RoleSlug, hierarchyLevelLabel, roleHierarchyLevel, sortRolesByHierarchy, userHierarchyLevel } from '@lms/shared-types';
import { ALL_PERMISSIONS, PERMISSIONS, ROLE_LABELS, type Permission } from '@lms/shared-types';

export { ROLE_LABELS, ROLES, hierarchyLevelLabel, roleHierarchyLevel, sortRolesByHierarchy, userHierarchyLevel };
export type { RoleSlug };

export const SYSTEM_ROLE_NAMES: readonly string[] = Object.values(ROLES);

export const ROLE_ICONS: Record<string, string> = {
  [ROLES.DEVELOPER]: 'code',
  [ROLES.ADMIN]: 'manage_accounts',
  [ROLES.STUDENT]: 'school',
  [ROLES.SUPER_ADMIN]: 'shield_person',
  [ROLES.TEACHER]: 'person_pin_circle',
  [ROLES.SEREMITT]: 'visibility',
};

export function roleLabel(name: string, label?: string | null): string {
  const localized = ROLE_LABELS[name as RoleSlug];
  if (localized) return localized;
  return label ?? name;
}

export function roleIcon(name: string): string {
  return ROLE_ICONS[name] ?? 'badge';
}

export function isSystemRole(name: string): boolean {
  return SYSTEM_ROLE_NAMES.includes(name);
}

export const PERMISSION_LABELS: Record<Permission, string> = {
  [PERMISSIONS.DASHBOARD_ACCESS]: 'Acceder al panel',
  [PERMISSIONS.USUARIOS_VER]: 'Ver usuarios',
  [PERMISSIONS.USUARIOS_CREAR]: 'Crear usuarios',
  [PERMISSIONS.USUARIOS_EDITAR]: 'Editar usuarios',
  [PERMISSIONS.USUARIOS_ELIMINAR]: 'Eliminar usuarios',
  [PERMISSIONS.ESTUDIANTES_PERFIL_VER]: 'Ver perfil de estudiantes',
  [PERMISSIONS.CAMPOS_USUARIO_VER]: 'Ver campos personalizados de usuario',
  [PERMISSIONS.CAMPOS_USUARIO_CREAR]: 'Crear campos personalizados de usuario',
  [PERMISSIONS.CAMPOS_USUARIO_EDITAR]: 'Editar campos personalizados de usuario',
  [PERMISSIONS.CAMPOS_USUARIO_ELIMINAR]: 'Eliminar campos personalizados de usuario',
  [PERMISSIONS.ROLES_VER]: 'Ver roles',
  [PERMISSIONS.ROLES_CREAR]: 'Crear roles',
  [PERMISSIONS.ROLES_EDITAR]: 'Editar roles',
  [PERMISSIONS.ROLES_ELIMINAR]: 'Eliminar roles',
  [PERMISSIONS.PERMISOS_VER]: 'Ver permisos',
  [PERMISSIONS.PERMISOS_EDITAR]: 'Editar permisos',
  [PERMISSIONS.PERFIL_VER]: 'Ver perfil',
  [PERMISSIONS.PERFIL_EDITAR]: 'Editar perfil',
  [PERMISSIONS.CATEGORIAS_VER]: 'Ver categorías',
  [PERMISSIONS.CATEGORIAS_CREAR]: 'Crear categorías',
  [PERMISSIONS.CATEGORIAS_EDITAR]: 'Editar categorías',
  [PERMISSIONS.CATEGORIAS_ELIMINAR]: 'Eliminar categorías',
  [PERMISSIONS.CURSOS_VER]: 'Ver cursos',
  [PERMISSIONS.CURSOS_CREAR]: 'Crear cursos',
  [PERMISSIONS.CURSOS_EDITAR]: 'Editar cursos',
  [PERMISSIONS.CURSOS_ELIMINAR]: 'Eliminar cursos',
  [PERMISSIONS.CURSOS_SUPERVISAR]: 'Supervisar cursos (solo lectura)',
  [PERMISSIONS.BIBLIOTECA_VER]: 'Ver biblioteca',
  [PERMISSIONS.BIBLIOTECA_CREAR]: 'Crear objetos',
  [PERMISSIONS.BIBLIOTECA_EDITAR]: 'Editar objetos',
  [PERMISSIONS.BIBLIOTECA_ELIMINAR]: 'Eliminar objetos',
  [PERMISSIONS.CONSTRUCTOR_VER]: 'Ver constructor',
  [PERMISSIONS.CONSTRUCTOR_EDITAR]: 'Editar constructor',
  [PERMISSIONS.MULTIMEDIA_VER]: 'Ver multimedia',
  [PERMISSIONS.MULTIMEDIA_CREAR]: 'Subir archivos',
  [PERMISSIONS.MULTIMEDIA_EDITAR]: 'Editar archivos',
  [PERMISSIONS.MULTIMEDIA_ELIMINAR]: 'Eliminar archivos',
  [PERMISSIONS.H5P_VER]: 'Ver actividades H5P',
  [PERMISSIONS.H5P_IMPORTAR]: 'Importar paquetes H5P',
  [PERMISSIONS.H5P_EDITAR]: 'Editar actividades H5P',
  [PERMISSIONS.H5P_ELIMINAR]: 'Eliminar actividades H5P',
  [PERMISSIONS.H5P_PUBLICAR]: 'Publicar versiones H5P',
  [PERMISSIONS.H5P_RESTAURAR]: 'Restaurar versiones H5P',
  [PERMISSIONS.H5P_DEPENDENCIAS_VER]: 'Ver dependencias de librerías H5P',
  [PERMISSIONS.EVALUACIONES_VER]: 'Ver evaluaciones',
  [PERMISSIONS.EVALUACIONES_INTENTAR]: 'Rendir evaluaciones',
  [PERMISSIONS.EVALUACIONES_CREAR]: 'Crear evaluaciones',
  [PERMISSIONS.EVALUACIONES_EDITAR]: 'Editar evaluaciones',
  [PERMISSIONS.EVALUACIONES_ELIMINAR]: 'Eliminar evaluaciones',
  [PERMISSIONS.EVALUACIONES_PUBLICAR]: 'Publicar evaluaciones',
  [PERMISSIONS.EVALUACIONES_RESTAURAR]: 'Restaurar evaluaciones',
  [PERMISSIONS.EVALUACIONES_INTENTOS_GESTIONAR]: 'Gestionar intentos de evaluación',
  [PERMISSIONS.PREGUNTAS_VER]: 'Ver banco de preguntas',
  [PERMISSIONS.PREGUNTAS_CREAR]: 'Crear preguntas',
  [PERMISSIONS.PREGUNTAS_EDITAR]: 'Editar preguntas',
  [PERMISSIONS.PREGUNTAS_ELIMINAR]: 'Eliminar preguntas',
  [PERMISSIONS.PROGRESO_VER]: 'Ver progreso de aprendizaje',
  [PERMISSIONS.PROGRESO_TIEMPO_VER]: 'Ver tiempo de conexión de estudiantes',
  [PERMISSIONS.PROGRESO_ADMINISTRAR]: 'Administrar progreso de estudiantes',
  [PERMISSIONS.MIS_CURSOS_VER]: 'Ver mis cursos y mis calificaciones',
  [PERMISSIONS.CALIFICACIONES_VER]: 'Ver libro de calificaciones',
  [PERMISSIONS.CALIFICACIONES_ADMINISTRAR]: 'Administrar calificaciones',
  [PERMISSIONS.CALIFICACIONES_EDITAR]: 'Editar y corregir calificaciones',
  [PERMISSIONS.EXPEDIENTE_VER]: 'Ver expediente y calificaciones propias',
  [PERMISSIONS.EXPEDIENTE_VER_TODOS]: 'Ver todos los expedientes',
  [PERMISSIONS.EXPEDIENTE_ADMINISTRAR]: 'Administrar expedientes académicos',
  [PERMISSIONS.INSCRIPCIONES_VER]: 'Ver matrículas',
  [PERMISSIONS.INSCRIPCIONES_ADMINISTRAR]: 'Administrar matrículas',
  [PERMISSIONS.INSCRIPCIONES_ASIGNAR]: 'Asignar estudiantes a cursos',
  [PERMISSIONS.GRUPOS_VER]: 'Ver grupos',
  [PERMISSIONS.GRUPOS_ADMINISTRAR]: 'Administrar grupos',
  [PERMISSIONS.CREDENCIALES_VER]: 'Ver credenciales',
  [PERMISSIONS.CREDENCIALES_ADMINISTRAR]: 'Administrar credenciales',
  [PERMISSIONS.CREDENCIALES_EMITIR]: 'Emitir credenciales',
  [PERMISSIONS.CREDENCIALES_PLANTILLAS]: 'Gestionar plantillas',
  [PERMISSIONS.MIS_CREDENCIALES_VER]: 'Ver mis credenciales',
  [PERMISSIONS.CONTRATOS_VER]: 'Ver contratos',
  [PERMISSIONS.CONTRATOS_PLANTILLAS]: 'Gestionar plantillas de contrato',
  [PERMISSIONS.CONTRATOS_ADMINISTRAR]: 'Administrar contratos',
  [PERMISSIONS.MIS_CONTRATOS_VER]: 'Ver mis contratos',
  [PERMISSIONS.MIS_SOPORTE_VER]: 'Ver mis tickets de soporte',
  [PERMISSIONS.SOPORTE_VER]: 'Ver mesa de ayuda',
  [PERMISSIONS.SOPORTE_ADMINISTRAR]: 'Administrar mesa de ayuda',
  [PERMISSIONS.POLITICAS_LEGALES_VER]: 'Ver políticas legales',
  [PERMISSIONS.POLITICAS_LEGALES_ADMINISTRAR]: 'Administrar políticas legales',
  [PERMISSIONS.APARIENCIA_VER]: 'Ver apariencia del sitio',
  [PERMISSIONS.APARIENCIA_ADMINISTRAR]: 'Administrar apariencia del sitio',
  [PERMISSIONS.DESARROLLO_VER]: 'Ver herramientas de desarrollo',
};

export function permissionLabel(name: string): string {
  return PERMISSION_LABELS[name as Permission] ?? name;
}

const GROUP_META: Record<string, { label: string; icon: string }> = {
  biblioteca: { label: 'Biblioteca', icon: 'library_books' },
  calificaciones: { label: 'Calificaciones', icon: 'grade' },
  usuarios: { label: 'Usuarios', icon: 'group' },
  categorias: { label: 'Categorías', icon: 'category' },
  constructor: { label: 'Constructor', icon: 'build' },
  credenciales: { label: 'Credenciales', icon: 'verified' },
  cursos: { label: 'Cursos', icon: 'school' },
  'mis-cursos': { label: 'Área del estudiante', icon: 'person' },
  general: { label: 'General', icon: 'dashboard' },
  apariencia: { label: 'Apariencia', icon: 'palette' },
  desarrollo: { label: 'Desarrollo', icon: 'terminal' },
  evaluaciones: { label: 'Evaluaciones', icon: 'assignment' },
  soporte: { label: 'Soporte', icon: 'support_agent' },
  contratos: { label: 'Contratos', icon: 'description' },
  'politicas-legales': { label: 'Políticas legales', icon: 'gavel' },
  h5p: { label: 'H5P', icon: 'extension' },
  multimedia: { label: 'Multimedia', icon: 'movie' },
  preguntas: { label: 'Preguntas', icon: 'quiz' },
};

const MOCK_GROUP_KEYS = [
  'biblioteca', 'calificaciones', 'usuarios', 'categorias', 'constructor',
  'credenciales', 'cursos', 'mis-cursos', 'general', 'evaluaciones', 'soporte',
  'contratos', 'politicas-legales', 'apariencia', 'desarrollo', 'h5p', 'multimedia', 'preguntas',
] as const;

const MERGE_INTO: Record<string, string> = {
  roles: 'usuarios',
  permisos: 'usuarios',
  perfil: 'usuarios',
  expediente: 'usuarios',
  grupos: 'usuarios',
  progreso: 'calificaciones',
  inscripciones: 'cursos',
  'mis-credenciales': 'credenciales',
  'mis-soporte': 'soporte',
  'mis-contratos': 'contratos',
};

const EXCLUDED = new Set(['otros']);

function permissionGroupKey(permission: string): string {
  const prefix = permission.split('.')[0] ?? 'general';
  if (prefix === 'dashboard') return 'general';
  if (prefix === 'campos-usuario') return 'usuarios';
  return prefix;
}

export interface PermissionCategory {
  key: string;
  label: string;
  icon: string;
  permissions: string[];
}

export function buildPermissionCategories(): PermissionCategory[] {
  const merged = new Map<string, Set<string>>();
  for (const key of MOCK_GROUP_KEYS) merged.set(key, new Set());

  for (const permission of ALL_PERMISSIONS) {
    const rawGroup = permissionGroupKey(permission);
    if (EXCLUDED.has(rawGroup)) continue;

    const targetKey = MOCK_GROUP_KEYS.includes(rawGroup as (typeof MOCK_GROUP_KEYS)[number])
      ? rawGroup
      : (MERGE_INTO[rawGroup] ?? 'general');

    if (!merged.has(targetKey)) merged.set(targetKey, new Set());
    merged.get(targetKey)!.add(permission);
  }

  return MOCK_GROUP_KEYS.map((key) => ({
    key,
    label: GROUP_META[key]?.label ?? key,
    icon: GROUP_META[key]?.icon ?? 'settings',
    permissions: [...(merged.get(key) ?? [])].sort((a, b) =>
      permissionLabel(a).localeCompare(permissionLabel(b), 'es'),
    ),
  })).filter((cat) => cat.permissions.length > 0);
}

export const PERMISSION_CATEGORIES = buildPermissionCategories();
